"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { motion } from "framer-motion";
import { PageAffiliateOldDesignSchema } from "@/sanity/pages/PageAffiliateOldDesignTemplate";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import { useCallback, useEffect, useState } from "react";

export default function PayoutsSection({
  data,
  className,
}: {
  data: PageAffiliateOldDesignSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  return (
    <section className={cn("relative overflow-clip pb-[7rem] lg:pb-[20rem]", className)}>
      <div className="container flex flex-col items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <ChipSerif text={data.payoutsChip} />
        </motion.div>

        {data?.payoutsText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start lg:richtext-center w-[min(95rem,100%)] flex flex-col gap-[1.5rem]"
          >
            <PortableText value={data.payoutsText} components={richTextComponent} />
          </motion.div>
        )}
      </div>
      <div className="w-full">
        {data?.payoutsItems && data.payoutsItems.length > 0 && (
          <motion.div
            className="mt-[2.5rem] lg:mt-[4.75rem] w-full mb-[5rem] lg:mb-[8rem]"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Carousel
              setApi={setApi}
              opts={{ align: "center", loop: true }}
              className="w-full"
            >
              <CarouselContent>
                {data.payoutsItems.map((item, index) => (
                  <CarouselItem
                    key={index}
                    className="px-[1rem] lg:pr-0 lg:pl-[3rem] basis-full lg:basis-[134.875rem]"
                  >
                    <div
                      className="h-full flex flex-col justify-between items-center w-full lg:flex-row bg-[#FCFDFD] border border-black/[0.02] p-[3rem] rounded-[2.5rem]"
                      style={{ boxShadow: "0 -1.257px 5.027px 0 rgba(0, 0, 0, 0.10) inset" }}
                    >
                      <div className="w-[min(59.625rem,100%)] lg:px-[4.5rem] lg:py-[5rem]">
                        {item.chip?.text && (
                          <ChipSerif text={item.chip.text} className="mb-[3rem]" />
                        )}
                        {item.text && (
                          <div className="mb-[3rem] lg:mb-[7rem] richtext richtext-start prose-p:text-[#103037] prose-p:font-medium prose-p:text-[3rem] flex flex-col gap-[1.5rem]">
                            <PortableText value={item.text} components={richTextComponent} />
                          </div>
                        )}
                        {item.name && (
                          <p className="text-[#313131]/60 text-[2rem] leading-[130%] mb-0">
                            {item.name}
                          </p>
                        )}
                      </div>

                      {item.image && (
                        <div className="w-[min(68.625rem,100%)] overflow-hidden relative rounded-[2rem] mt-[6rem] lg:mt-0">
                          <Image
                            src={item.image}
                            alt={item.name || "payout"}
                            className="object-cover rounded-[2rem]"
                          />
                        </div>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            <div className="flex items-center gap-[1.5rem] mt-[2.5rem] lg:mt-[3.5rem] w-fit mx-auto">
              <button
                onClick={() => api?.scrollPrev()}
                disabled={current === 0}
                className="flex w-[4rem] h-[4rem] p-[1rem] justify-center items-center gap-[1rem] rounded-[0.75rem] shrink-0 transition-all duration-200"
                style={{ backgroundColor: current === 0 ? "#1030371A" : "#103037" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect width="20" height="20" fill="white" fillOpacity="0.01" style={{ mixBlendMode: "multiply" }} />
                  <path d="M6.25 10L12.5 3.75L13.375 4.625L8 10L13.375 15.375L12.5 16.25L6.25 10Z" fill={current === 0 ? "#103037" : "white"} />
                </svg>
              </button>

              <div className="flex gap-[0.75rem]">
                {data.payoutsItems.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollTo(index)}
                    className="h-[0.375rem] rounded-full transition-all duration-500 w-[5.75rem]"
                    style={{ backgroundColor: current === index ? "#6DA991" : "#6DA99166" }}
                  />
                ))}
              </div>

              <button
                onClick={() => api?.scrollNext()}
                disabled={current === count - 1}
                className="flex w-[4rem] h-[4rem] p-[1rem] justify-center items-center gap-[1rem] rounded-[0.75rem] shrink-0 transition-all duration-200"
                style={{ backgroundColor: current === count - 1 ? "#1030371A" : "#103037" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect width="20" height="20" fill="white" fillOpacity="0.01" style={{ mixBlendMode: "multiply" }} />
                  <path d="M13.75 10L7.5 16.25L6.625 15.375L12 10L6.625 4.625L7.5 3.75L13.75 10Z" fill={current === count - 1 ? "#103037" : "white"} />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
